import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";

// Bir field listesi için varsayılan değerlerden başlangıç objesi üretir.
export function buildDefaults(fields) {
  const out = {};
  for (const f of fields || []) {
    if (f.type === "select" && f.default) out[f.key] = f.default;
    else if (f.type === "multiselect" && Array.isArray(f.default) && f.default.length) out[f.key] = f.default;
    else if (f.type === "boolean") out[f.key] = !!f.default;
  }
  return out;
}

// Saf özyinelemeli alan render fonksiyonu (kendi bileşen adını JSX tag olarak KULLANMAZ).
function renderField(field, value, onChange, testId) {
  const { type, label, options = [], fields = [] } = field;

  if (type === "text") {
    return (
      <div key={field.key}>
        <Label>{label}</Label>
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`${testId}-input`} />
      </div>
    );
  }

  if (type === "number") {
    return (
      <div key={field.key}>
        <Label>{label}</Label>
        <Input type="number" step="any" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={`${testId}-input`} />
      </div>
    );
  }

  if (type === "select") {
    return (
      <div key={field.key}>
        <Label>{label}</Label>
        <Select value={value || "__none"} onValueChange={(val) => onChange(val === "__none" ? "" : val)}>
          <SelectTrigger data-testid={`${testId}-select`}><SelectValue placeholder="Seçin" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none">—</SelectItem>
            {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (type === "multiselect") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (o) => onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);
    return (
      <div key={field.key}>
        <Label>{label}</Label>
        <div className="flex flex-wrap gap-2 mt-1" data-testid={`${testId}-multiselect`}>
          {options.length === 0 && <span className="text-xs text-zinc-400">Seçenek tanımlı değil</span>}
          {options.map((o) => (
            <label key={o} className="inline-flex items-center gap-1.5 border border-zinc-200 rounded px-2 py-1 text-sm cursor-pointer hover:bg-zinc-50">
              <Checkbox checked={arr.includes(o)} onCheckedChange={() => toggle(o)} />
              {o}
            </label>
          ))}
        </div>
      </div>
    );
  }

  if (type === "boolean") {
    return (
      <label key={field.key} className="flex items-center gap-2 cursor-pointer py-1">
        <Checkbox checked={!!value} onCheckedChange={(c) => onChange(!!c)} data-testid={`${testId}-bool`} />
        <span className="text-sm">{label}</span>
      </label>
    );
  }

  if (type === "object") {
    const instances = Array.isArray(value) ? value : [];
    const add = () => onChange([...instances, buildDefaults(fields)]);
    const setSub = (i, sf, val) => onChange(instances.map((inst, idx) => (idx === i ? { ...(inst || {}), [sf.key]: val } : inst)));
    const remove = (i) => onChange(instances.filter((_, idx) => idx !== i));
    return (
      <div key={field.key} className="border border-zinc-200 rounded-md p-3 bg-zinc-50/60">
        <div className="flex items-center justify-between mb-2">
          <Label className="font-medium">{label}</Label>
          <Button type="button" size="sm" variant="outline" onClick={add} data-testid={`${testId}-add`}>
            <Plus size={13} className="mr-1" />Ekle
          </Button>
        </div>
        {instances.length === 0 && <div className="text-xs text-zinc-400">Henüz eklenmedi</div>}
        <div className="space-y-2">
          {instances.map((inst, i) => (
            <div key={i} className="border border-zinc-200 rounded bg-white p-2.5" data-testid={`${testId}-instance-${i}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-medium text-zinc-500">{label} #{i + 1}</span>
                <button type="button" onClick={() => remove(i)} className="text-zinc-400 hover:text-red-600" data-testid={`${testId}-remove-${i}`}>
                  <Trash2 size={13} />
                </button>
              </div>
              <div className="space-y-3">
                {(fields || []).map((sf) => renderField(sf, (inst || {})[sf.key], (val) => setSub(i, sf, val), `${testId}-${i}-${sf.key}`))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return null;
}

// Dinamik imalat detay formu — kategori şemasına göre değer düzenler.
export default function DynamicDetailsForm({ fields, value, onChange, testIdPrefix = "detail" }) {
  const v = value || {};
  const set = (key, val) => onChange({ ...v, [key]: val });
  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-form`}>
      {(fields || []).map((f) => renderField(f, v[f.key], (val) => set(f.key, val), `${testIdPrefix}-${f.key}`))}
    </div>
  );
}
